import { useState, useRef, useEffect, useMemo, lazy, Suspense } from "react";
import { Avatar } from "../components/Avatar";
import { DesktopNav } from "../components/DesktopNav";
import { MainResultCard } from "../components/MainResultCard";
import { StatCard } from "../components/StatCard";
import { ReadinessScore } from "../components/ReadinessScore";
import { ReadinessBreakdown } from "../components/ReadinessBreakdown";
import { RecommendationCard } from "../components/RecommendationCard";
import {
  DashboardLoading,
  DashboardProfileError,
  DashboardProjectionsError,
  DashboardCalculating,
} from "../components/DashboardStatusScreens";
import { FinancialSummary } from "../components/FinancialSummary";
import { ScenarioSelector } from "../components/ScenarioSelector";
import { ScenarioComparison } from "../components/ScenarioComparison";
import { UndoToastStack } from "../components/UndoToast";
import type { ScenarioPreview } from "../types/scenario";
import { FeatureLockedContent } from "../components/FeatureGates";
import { UpgradePrompt } from "../components/UpgradePrompt";
import { TrialBanner } from "../components/TrialBanner";
import { WhatIfPanel } from "../components/WhatIfPanel";
import { useDashboardLogic } from "./Dashboard.logic";
import { useProjections } from "../hooks/useProjections";
import { useScenarios } from "../hooks/useScenarios";
import { useTrial } from "../hooks/useTrial";
import { useWhatIfScenario } from "../hooks/useWhatIfScenario";
import { useMetricDeltas } from "../hooks/useMetricDeltas";
import { useDashboardHandlers } from "../hooks/useDashboardHandlers";
import { generateRecommendations } from "../utils/recommendations";
import { getReadinessCopy } from "../utils/dashboardCopy";
import {
  formatCurrency,
  displayYears,
  displayPercentage,
} from "../utils/formatting";
import { Plan } from "../types/user";

const PortfolioChart = lazy(async () => {
  const module = await import("../components/PortfolioChart");
  return { default: module.PortfolioChart };
});

/**
 * Dashboard - Desktop implementation
 *
 * Desktop-specific dashboard where settings are accessed via modal.
 * Includes keyboard navigation and accessibility features for desktop users.
 */
function DashboardWeb() {
  const {
    user,
    userProfile,
    age,
    loading: authLoading,
    error: authError,
    handleSignOut,
    navigate,
  } = useDashboardLogic();

  // Trial banner state
  const trial = useTrial(userProfile?.createdAt);

  // Fetch retirement projections from Firestore
  const {
    data,
    result,
    netWorthAtRetirement,
    liquidAssetsAtRetirement,
    liabilitiesAtRetirement,
    totals,
    assetBreakdown,
    loading: projectionsLoading,
    error: projectionsError,
  } = useProjections(user?.userId);

  // Desktop-specific state: menu with keyboard navigation
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [focusedMenuIndex, setFocusedMenuIndex] = useState(-1);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuItemsRef = useRef<(HTMLButtonElement | null)[]>([]);
  const recommendationsRef = useRef<HTMLDivElement>(null);

  // Scenario management
  const basePlan = userProfile?.plan ?? Plan.fromName("basic");
  const maxScenarios = basePlan.maxScenarios;
  const {
    scenarios,
    scenariosLoading,
    scenariosError,
    activeScenarioName,
    isCurrentSettingsSaved,
    pendingDeletes,
    saveScenario,
    loadScenario,
    deleteScenario,
    undoDelete,
    confirmDelete,
  } = useScenarios(user?.userId, maxScenarios);

  // What-If scenario mode
  const whatIf = useWhatIfScenario(data);

  // Metric deltas after settings changes
  const metricDeltas = useMetricDeltas(whatIf.isActive);

  // Shared handlers (comparison, what-if save, smart defaults, save scenario)
  const {
    handleWhatIfSave,
    comparisonScenarioIds,
    openComparison,
    closeComparison,
    handleCompareViewDetails,
    handleSaveScenario,
  } = useDashboardHandlers({
    userId: user?.userId ?? "",
    whatIf,
    loadScenario,
    saveScenario,
  });

  const handleAvatarClick = () => {
    setIsMenuOpen(!isMenuOpen);
    setFocusedMenuIndex(-1); // Reset focus when opening
  };

  const handleSettingsClick = () => {
    setIsMenuOpen(false);
    navigate("/settings", { state: { category: "profile" } });
  };

  // Desktop-specific: Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Desktop-specific: Handle Escape key to close dropdown
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, []);

  // Desktop-specific: Handle keyboard navigation in dropdown menu
  useEffect(() => {
    if (!isMenuOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      const menuItems = ["settings", "signout"];

      switch (event.key) {
        case "ArrowDown":
          event.preventDefault();
          setFocusedMenuIndex((prev) => (prev + 1) % menuItems.length);
          break;
        case "ArrowUp":
          event.preventDefault();
          setFocusedMenuIndex(
            (prev) => (prev - 1 + menuItems.length) % menuItems.length
          );
          break;
        case "Enter":
        case " ":
          event.preventDefault();
          // Trigger click on focused item
          if (menuItemsRef.current[focusedMenuIndex]) {
            menuItemsRef.current[focusedMenuIndex]?.click();
          }
          break;
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isMenuOpen, focusedMenuIndex]);

  // Desktop-specific: Focus the menu item when focusedMenuIndex changes
  useEffect(() => {
    if (isMenuOpen && menuItemsRef.current[focusedMenuIndex]) {
      menuItemsRef.current[focusedMenuIndex]?.focus();
    }
  }, [focusedMenuIndex, isMenuOpen]);

  // Original slider values for What-If comparison display (F-06: memoized)
  // Must be before early returns to satisfy React hooks rules
  const originalSliderValues = useMemo(
    () =>
      data
        ? {
            retirementAge: data.retirementAge,
            lifeExpectancy: data.lifeExpectancy,
            expectedReturnRate: data.annualReturn,
            inflationRate: data.inflationRate,
            expensesAtRetirement: data.expensesAtRetirement,
          }
        : undefined,
    [data]
  );

  // Show loading while auth or projections are loading
  if (authLoading || projectionsLoading) {
    return <DashboardLoading />;
  }

  // Auth and profile loading complete - check if we have profile
  if (!userProfile) {
    return (
      <DashboardProfileError error={authError} onSignOut={handleSignOut} />
    );
  }

  // Check for projections error
  if (projectionsError) {
    return (
      <DashboardProjectionsError
        error={projectionsError}
        onRetry={() => window.location.reload()}
      />
    );
  }

  // Check if we have data and result before rendering dashboard
  if (!data || !result) {
    return <DashboardCalculating />;
  }

  // When scenario mode is active, use scenario values for display
  const activeData =
    whatIf.isActive && whatIf.scenarioData ? whatIf.scenarioData : data;
  const activeResult =
    whatIf.isActive && whatIf.scenarioResult ? whatIf.scenarioResult : result;

  return (
    <div className="dashboard-shell" id="dashboard-web">
      {/* Header */}
      <header className="header">
        <div className="header-content">
          <h1 className="logo">RetireEZ</h1>

          {/* Desktop navigation links */}
          <DesktopNav />

          {/* Avatar with dropdown menu */}
          <div className="avatar-dropdown-container" ref={menuRef}>
            <button
              onClick={handleAvatarClick}
              className="avatar-menu-trigger"
              aria-label="User menu"
              aria-expanded={isMenuOpen}
              aria-haspopup="true"
            >
              <Avatar profile={userProfile} size="small" />
              <span className="avatar-menu-chevron" aria-hidden="true">
                ▼
              </span>
            </button>

            {/* Dropdown menu with keyboard navigation support */}
            {isMenuOpen && (
              <div className="user-dropdown">
                {/* User info section */}
                <div className="user-dropdown-info">
                  <div className="user-dropdown-name">
                    {userProfile.fullName()}
                  </div>
                  <div className="user-dropdown-email">{user?.email}</div>
                </div>

                {/* Settings option */}
                <button
                  ref={(el) => {
                    menuItemsRef.current[0] = el;
                  }}
                  onClick={handleSettingsClick}
                  className={
                    focusedMenuIndex === 0
                      ? "user-dropdown-item-focused"
                      : "user-dropdown-item"
                  }
                  aria-label="Settings"
                >
                  Settings
                </button>

                {/* Sign Out button */}
                <div className="dashboard-dropdown-separator">
                  <button
                    ref={(el) => {
                      menuItemsRef.current[1] = el;
                    }}
                    onClick={handleSignOut}
                    className={
                      focusedMenuIndex === 1
                        ? "user-dropdown-item-signout-focused"
                        : "user-dropdown-item-signout"
                    }
                    aria-label="Sign out"
                  >
                    Sign Out
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Trial Banner — between header and main content */}
      {trial.showBanner && (
        <TrialBanner
          variant="banner"
          trialDay={trial.trialDay}
          totalDays={trial.totalDays}
          onUpgrade={() => navigate("/pricing")}
          onDismiss={trial.handleDismiss}
          dismissible={trial.dismissible}
        />
      )}

      {/* Blocking upgrade modal when trial expired */}
      {trial.showBlockingModal && (
        <UpgradePrompt
          featureId="trial-expired"
          title="Your Free Trial Has Ended"
          description="Upgrade to continue using all RetireEZ features."
          buttonText="Compare Plans"
        />
      )}

      {/* Scenario Mode Banner — sticky below header when What-If is active */}
      {whatIf.isActive && (
        <div className="what-if-scenario-banner" id="scenario-mode-banner">
          <span>⚡ Scenario Mode — exploring what-if changes (not saved)</span>
          <button
            type="button"
            className="what-if-scenario-banner-exit"
            onClick={whatIf.reset}
          >
            Exit
          </button>
        </div>
      )}

      {/* Main Content */}
      <div className="main-content">
        {/* Dashboard Grid */}
        <div className="dashboard-container-web">
          {/* Hero Metric - Main Result Card */}
          <div className="dashboard-section">
            {/* Scenario Selector — left-aligned above the card */}
            <div className="dashboard-header-row">
              <div className="dashboard-z-layer">
                <ScenarioSelector
                  activeScenarioName={activeScenarioName}
                  isCurrentSettingsSaved={isCurrentSettingsSaved}
                  scenarios={scenarios}
                  isLoading={scenariosLoading}
                  error={scenariosError ?? undefined}
                  preview={{
                    retirementAge: activeData.retirementAge,
                    finalValue:
                      netWorthAtRetirement ?? activeResult.finalAmount,
                    monthlyIncome: activeResult.monthlyIncome,
                    readinessScore: activeResult.readinessScore,
                    shortTermHealthScore: activeResult.shortTermHealthScore,
                    liquidReservesScore: activeResult.liquidReservesScore,
                    cashFlowMarginScore: activeResult.cashFlowMarginScore,
                    lifestyleScore: activeResult.lifestyleScore,
                    currency: activeData.currency,
                  }}
                  onLoad={async (scenario) => {
                    await loadScenario(scenario.id);
                  }}
                  onDelete={async (scenarioId) => {
                    await deleteScenario(scenarioId);
                  }}
                  onSave={handleSaveScenario}
                  onCompare={openComparison}
                  maxScenarios={maxScenarios}
                  disabled={whatIf.isActive}
                  disabledTooltip="Exit Scenario Mode to manage scenarios"
                />
              </div>
            </div>

            <MainResultCard
              retirementAge={activeData.retirementAge}
              finalAmount={netWorthAtRetirement ?? activeResult.finalAmount}
              currency={activeData.currency}
              subtitle="Projected net worth — all assets minus liabilities at retirement"
              liquidAssets={liquidAssetsAtRetirement}
              liabilities={liabilitiesAtRetirement}
              readinessCopy={getReadinessCopy(activeResult.readinessScore)}
              isScenarioMode={whatIf.isActive}
              onViewDetails={() => navigate("/projections")}
              onSeeRecommendations={() =>
                recommendationsRef.current?.scrollIntoView({
                  behavior: "smooth",
                })
              }
            />
          </div>

          {/* Financial Summary Section */}
          {totals && userProfile && (
            <div className="dashboard-section">
              <div className="dashboard-section-header">
                <h2>Financial Summary</h2>
              </div>
              <FinancialSummary
                totals={totals}
                currentAge={age}
                currency={data.currency}
              />
            </div>
          )}

          {/* Key Metrics Section */}
          <div className="dashboard-section">
            <div className="dashboard-section-header">
              <h2>Key Metrics</h2>
            </div>
            <div className="dashboard-grid-layout">
              {/* Left Column: 4 Stat Cards */}
              <div className="dashboard-grid-main">
                <StatCard
                  label="Years to Retirement"
                  value={displayYears(activeResult.yearsToRetirement)}
                  icon="⏳"
                  color="blue"
                  deltaInfo={
                    metricDeltas.deltas?.yearsToRetirement
                      ? {
                          delta: metricDeltas.deltas.yearsToRetirement,
                          type: "score",
                          stagger: 0,
                        }
                      : undefined
                  }
                />
                <StatCard
                  label="Monthly Retirement Income"
                  value={formatCurrency(
                    activeResult.monthlyIncome,
                    activeData.currency
                  )}
                  subtitle={
                    activeResult.monthlyIncome === 0
                      ? "Enter your income in Settings"
                      : activeData.expensesAtRetirement === 0
                        ? undefined
                        : `${activeData.expensesAtRetirement > 0 ? "+" : ""}${displayPercentage(activeData.expensesAtRetirement)} expense change`
                  }
                  icon="💰"
                  color="green"
                  deltaInfo={
                    metricDeltas.deltas?.monthlyIncome
                      ? {
                          delta: metricDeltas.deltas.monthlyIncome,
                          type: "currency",
                          stagger: 1,
                        }
                      : undefined
                  }
                />
                <StatCard
                  label="Savings Rate"
                  value={
                    activeResult.savingsRate !== undefined
                      ? displayPercentage(activeResult.savingsRate / 100)
                      : "---"
                  }
                  subtitle={
                    activeResult.savingsRate === undefined
                      ? "Enter income and savings data"
                      : undefined
                  }
                  icon="📊"
                  color="purple"
                  deltaInfo={
                    metricDeltas.deltas?.savingsRate
                      ? {
                          delta: metricDeltas.deltas.savingsRate,
                          type: "percentage",
                          stagger: 2,
                        }
                      : undefined
                  }
                />
                <StatCard
                  label="Post-Retirement Income Coverage"
                  value={displayPercentage(activeResult.incomeCoverage)}
                  icon="🛡️"
                  color={activeResult.incomeCoverage >= 1 ? "green" : "teal"}
                  deltaInfo={
                    metricDeltas.deltas?.incomeCoverage
                      ? {
                          delta: Math.round(
                            metricDeltas.deltas.incomeCoverage * 100
                          ),
                          type: "percentage",
                          stagger: 3,
                        }
                      : undefined
                  }
                />
              </div>

              {/* Right Column: Readiness Score Gauge + Breakdown */}
              <div className="readiness-container">
                <div className="readiness-stack">
                  <ReadinessScore
                    score={activeResult.readinessScore}
                    size="lg"
                  />
                  <ReadinessBreakdown
                    overallScore={activeResult.readinessScore}
                    shortTermHealthScore={activeResult.shortTermHealthScore}
                    emergencyFundScore={activeResult.liquidReservesScore}
                    cashFlowScore={activeResult.cashFlowMarginScore}
                    lifestyleScore={activeResult.lifestyleScore}
                    emergencyFundCurrent={assetBreakdown?.emergencyFund}
                    emergencyFundTarget={
                      activeData ? activeData.monthlyExpenses * 6 : undefined
                    }
                    monthlyCashFlowSurplus={
                      totals ? totals.income - totals.expenses : undefined
                    }
                    currency={activeData?.currency}
                    onNavigateToSettings={(category) => {
                      navigate("/settings", { state: { category } });
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Portfolio Growth Chart - Gated Feature */}
          <FeatureLockedContent
            featureId="feature-investment-tracking"
            fallback={<UpgradePrompt featureId="feature-investment-tracking" />}
            blurIntensity={8}
          >
            <div className="chart-section">
              <div className="dashboard-section-header">
                <h2>Portfolio Growth Projection</h2>
              </div>
              <Suspense
                fallback={
                  <div className="chart-placeholder" aria-hidden="true" />
                }
              >
                <PortfolioChart />
              </Suspense>
            </div>
          </FeatureLockedContent>

          {/* What-If Scenario Panel */}
          <WhatIfPanel
            sliderValues={whatIf.sliderValues}
            originalValues={originalSliderValues}
            isActive={whatIf.isActive}
            currentAge={activeData.currentAge}
            onSliderChange={whatIf.setSliderValue}
            onReset={whatIf.reset}
            onSave={handleWhatIfSave}
          />

          {/* Recommendations Section */}
          <div className="dashboard-section" ref={recommendationsRef}>
            <div className="dashboard-section-header">
              <h2>Recommendations</h2>
            </div>
            <div className="dashboard-cards-grid">
              {generateRecommendations(activeData, activeResult).map(
                (rec, index) => (
                  <RecommendationCard
                    key={index}
                    title={rec.title}
                    description={rec.description}
                    keyActions={rec.keyActions}
                    type={rec.type}
                    onAction={
                      rec.settingsCategory
                        ? () => {
                            navigate("/settings", {
                              state: { category: rec.settingsCategory },
                            });
                          }
                        : undefined
                    }
                  />
                )
              )}
            </div>
          </div>
        </div>
      </div>

      <UndoToastStack
        toasts={pendingDeletes}
        onUndo={undoDelete}
        onExpire={confirmDelete}
        onDismiss={confirmDelete}
      />

      {comparisonScenarioIds &&
        (() => {
          const currentPreview: ScenarioPreview = {
            retirementAge: activeData.retirementAge,
            finalValue: netWorthAtRetirement ?? activeResult.finalAmount,
            monthlyIncome: activeResult.monthlyIncome,
            readinessScore: activeResult.readinessScore,
            shortTermHealthScore: activeResult.shortTermHealthScore,
            liquidReservesScore: activeResult.liquidReservesScore,
            cashFlowMarginScore: activeResult.cashFlowMarginScore,
            lifestyleScore: activeResult.lifestyleScore,
            currency: activeData.currency,
          };
          const comparisonScenarios = comparisonScenarioIds
            .map((id) => {
              if (id === "__current__") {
                return {
                  id: "__current__",
                  userId: user?.userId ?? "",
                  name: activeScenarioName ?? "Current Plan",
                  description: "",
                  settings: {} as never,
                  createdAt: new Date(),
                  updatedAt: new Date(),
                  preview: currentPreview,
                };
              }
              return scenarios.find((s) => s.id === id);
            })
            .filter(
              (s): s is NonNullable<typeof s> & { preview: ScenarioPreview } =>
                s !== undefined && s.preview !== undefined
            );

          if (comparisonScenarios.length < 2) return null;

          return (
            <>
              <div
                className="comparison-backdrop"
                onClick={closeComparison}
                role="presentation"
              />
              <div
                className="comparison-panel"
                role="dialog"
                aria-label="Scenario comparison"
              >
                <ScenarioComparison
                  scenarios={comparisonScenarios}
                  activeScenarioId={
                    comparisonScenarioIds.includes("__current__")
                      ? "__current__"
                      : undefined
                  }
                  onClose={closeComparison}
                  onViewDetails={handleCompareViewDetails}
                />
              </div>
            </>
          );
        })()}
    </div>
  );
}

export default DashboardWeb;
